import React, { useState } from 'react';
import { Plus, X, Edit2, Save, ChevronDown, ChevronUp, Trash2, Settings2 } from 'lucide-react';

/**
 * ParameterManager Component
 * Allows users to add, edit, and remove parameters in the report builder
 */
const ParameterManager = ({ parameters = [], onChange }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [newParameter, setNewParameter] = useState(null);
  const [formData, setFormData] = useState(null);

  const parameterTypes = [
    { value: 'string', label: 'Text' },
    { value: 'textarea', label: 'Multi-line Text' },
    { value: 'integer', label: 'Integer Number' },
    { value: 'decimal', label: 'Decimal Number' },
    { value: 'boolean', label: 'Yes/No (Checkbox)' },
    { value: 'date', label: 'Date' },
    { value: 'datetime', label: 'Date & Time' },
    { value: 'time', label: 'Time' },
    { value: 'select', label: 'Dropdown (Select)' }
  ];

  const getDefaultParameter = () => ({
    name: '',
    label: '',
    description: '',
    type: 'string',
    required: false,
    defaultValue: '',
    placeholder: '',
    options: []
  });

  const handleAddParameter = () => {
    const defaultParam = getDefaultParameter();
    setNewParameter(defaultParam);
    setFormData(defaultParam);
    setEditingIndex(-1);
  };

  const handleSaveNew = () => {
    if (!formData.name || !formData.label) {
      window.alert('Name and Label are required');
      return;
    }

    // Check for duplicate names
    if (parameters.some(p => p.name === formData.name)) {
      window.alert('Parameter name must be unique');
      return;
    }

    const updatedParameters = [...parameters, formData];
    onChange(updatedParameters);
    setNewParameter(null);
    setFormData(null);
    setEditingIndex(null);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setExpandedIndex(index);
    setFormData({ ...parameters[index] });
  };

  const handleSaveEdit = (index) => {
    const updatedParameters = [...parameters];
    updatedParameters[index] = formData;
    onChange(updatedParameters);
    setEditingIndex(null);
    setFormData(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setFormData(null);
    setNewParameter(null);
  };

  const handleDelete = (index) => {
    if (window.confirm('Are you sure you want to delete this parameter?')) {
      const updatedParameters = parameters.filter((_, i) => i !== index);
      onChange(updatedParameters);
    }
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const updatedParameters = [...parameters];
    [updatedParameters[index - 1], updatedParameters[index]] = 
      [updatedParameters[index], updatedParameters[index - 1]];
    onChange(updatedParameters);
  };

  const handleMoveDown = (index) => {
    if (index === parameters.length - 1) return;
    const updatedParameters = [...parameters];
    [updatedParameters[index], updatedParameters[index + 1]] = 
      [updatedParameters[index + 1], updatedParameters[index]];
    onChange(updatedParameters);
  };

  const renderParameterForm = (onSave, onCancel) => {
    if (!formData) return null;

    return (
      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Parameter Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., startDate"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
            />
            <p className="text-xs text-gray-500 mt-1">Used in filters: {`{{${formData.name || 'paramName'}}}`}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Label <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              placeholder="e.g., Start Date"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Help text shown to users"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
            >
              {parameterTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Default Value
            </label>
            {formData.type === 'boolean' ? (
              <select
                value={formData.defaultValue}
                onChange={(e) => setFormData({ ...formData, defaultValue: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
              >
                <option value="">No default</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            ) : (
              <input
                type={formData.type === 'integer' || formData.type === 'decimal' ? 'number' : formData.type === 'date' ? 'date' : 'text'}
                value={formData.defaultValue}
                onChange={(e) => setFormData({ ...formData, defaultValue: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
              />
            )}
          </div>

          <div className="flex items-end">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.required}
                onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Required</span>
            </label>
          </div>
        </div>

        {formData.type === 'select' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Options (one per line, format: value|label)
            </label>
            <textarea
              value={(formData.options || []).map(o => `${o.value}|${o.label}`).join('\n')}
              onChange={(e) => {
                const options = e.target.value.split('\n').filter(Boolean).map(line => {
                  const [value, label] = line.split('|');
                  return { value: value.trim(), label: (label || value).trim() };
                });
                setFormData({ ...formData, options });
              }}
              rows={4}
              placeholder="Open|Open\nClosed|Closed\nPending|Pending"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 font-mono text-sm"
            />
          </div>
        )}

        <div className="flex items-center gap-2 pt-2">
          <button
            onClick={onSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Settings2 className="w-5 h-5" />
          Report Parameters
        </h3>
        <button
          onClick={handleAddParameter}
          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
        >
          <Plus className="w-4 h-4" />
          Add Parameter
        </button>
      </div>

      {parameters.length === 0 && !newParameter && (
        <div className="text-center py-8 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
          <Settings2 className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400 mb-2">No parameters defined</p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            Parameters allow users to filter and customize reports at runtime
          </p>
        </div>
      )}

      {/* Existing Parameters */}
      {parameters.map((param, index) => (
        <div
          key={index}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
        >
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center gap-3 flex-1">
              <button
                onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              >
                {expandedIndex === index ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {param.label}
                  {param.required && <span className="text-red-500 ml-1">*</span>}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-3">
                  <code className="bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">{`{{${param.name}}}`}</code>
                  <span className="capitalize">{param.type}</span>
                  {param.defaultValue && <span>Default: {String(param.defaultValue)}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleEdit(index)}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-blue-600"
                title="Edit"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleMoveUp(index)}
                disabled={index === 0}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move Up"
              >
                ↑
              </button>
              <button
                onClick={() => handleMoveDown(index)}
                disabled={index === parameters.length - 1}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move Down"
              >
                ↓
              </button>
              <button
                onClick={() => handleDelete(index)}
                className="p-2 hover:bg-red-100 dark:hover:bg-red-900/20 rounded text-red-600"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {expandedIndex === index && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              {editingIndex === index ? (
                renderParameterForm(
                  () => handleSaveEdit(index),
                  handleCancelEdit
                )
              ) : (
                <div className="space-y-2 text-sm">
                  {param.description && (
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Description:</span>
                      <span className="ml-2 text-gray-600 dark:text-gray-400">{param.description}</span>
                    </div>
                  )}
                  {param.placeholder && (
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Placeholder:</span>
                      <span className="ml-2 text-gray-600 dark:text-gray-400">{param.placeholder}</span>
                    </div>
                  )}
                  {param.type === 'select' && param.options && param.options.length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Options:</span>
                      <div className="ml-2 mt-1 space-y-1">
                        {param.options.map((opt, i) => (
                          <div key={i} className="text-gray-600 dark:text-gray-400">
                            • {opt.label} ({opt.value})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* New Parameter Form */}
      {newParameter && editingIndex === -1 && (
        <div className="bg-white dark:bg-gray-800 border-2 border-blue-300 dark:border-blue-700 rounded-lg p-4">
          <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3">New Parameter</h4>
          {renderParameterForm(handleSaveNew, handleCancelEdit)}
        </div>
      )}

      {/* Usage Help */}
      {parameters.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">💡 Using Parameters in Filters</h4>
          <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
            Use parameter placeholders in section filters with double curly braces:
          </p>
          <div className="bg-white dark:bg-gray-800 rounded p-2 font-mono text-xs">
            <div>Field: "ExpectedCloseDate", Operator: "&gt;=", Value: "{`{{startDate}}`}"</div>
            <div>Field: "Status", Operator: "=", Value: "{`{{status}}`}"</div>
            <div>Field: "CustomerName", Operator: "LIKE", Value: "%{`{{searchTerm}}`}%"</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParameterManager;

